// src/pages/Glossary.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  TextField,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import BarChartIcon from "@mui/icons-material/BarChart";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PublicIcon from "@mui/icons-material/Public";
import InsightsIcon from "@mui/icons-material/Insights";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

// ── Brand Palette (matches site exactly) ──
const BRAND = {
  background: "#F3F6FB",
  primary: "#1E3A8A",
  secondary: "#3B82F6",
  secondarySoft: "#7d9bdbff",
  accentGray: "#4B5563",
  accentGreen: "#10B981",
  paper: "#FFFFFF",
  border: "#E5E7EB",
};

// ── Keyframes (same pattern as Home.tsx) ──
const KEYFRAMES = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(32px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes floatY {
    0%, 100% { transform: translateY(0px) rotate(var(--rot, 0deg)); }
    50%       { transform: translateY(-14px) rotate(var(--rot, 0deg)); }
  }
  @keyframes dividerGrow {
    from { width: 0; }
    to   { width: 80px; }
  }
  .fade-up { animation: fadeUp 0.65s cubic-bezier(0.22,1,0.36,1) both; }
  .d0 { animation-delay: 0ms; }
  .d1 { animation-delay: 100ms; }
  .d2 { animation-delay: 200ms; }
  .d3 { animation-delay: 300ms; }
`;

// ── Style injector (same pattern as Home.tsx) ──
const StyleInjector: React.FC = () => {
  useEffect(() => {
    if (document.getElementById("glossary-keyframes")) return;
    const el = document.createElement("style");
    el.id = "glossary-keyframes";
    el.textContent = KEYFRAMES;
    document.head.appendChild(el);
  }, []);
  return null;
};

// ── Floating background icon (same as Home.tsx FloatIcon) ──
const FloatIcon: React.FC<{
  icon: React.ReactNode;
  size?: number;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  rot?: string;
  duration?: number;
  color?: string;
}> = ({ icon, size = 140, top, bottom, left, right, rot = "0deg", duration = 7, color = BRAND.primary }) => (
  <Box
    sx={{
      position: "absolute",
      fontSize: size,
      color,
      opacity: 0.06,
      top, bottom, left, right,
      "--rot": rot,
      animation: `floatY ${duration}s ease-in-out infinite`,
      pointerEvents: "none",
      userSelect: "none",
    } as any}
  >
    {icon}
  </Box>
);

// ── Glossary data: only terms from the actual site pages ──
interface GlossaryTerm {
  term: string;
  tag: string;
  icon: React.ReactNode;
  definition: string;
}

const TERMS: GlossaryTerm[] = [
  {
    term: "Digital Transaction Formalisation",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "The process by which cash-based payments shift into digitally recorded transactions, creating traceable financial records for households and MSMEs. The research paper measures this using the Transaction Formalisation Index, which had a mean score of 0.67 across 114 respondents.",
  },
    {
    term: "Economic Growth",
    tag: "Research Paper · Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "The increase in a country's production of goods and services over time, typically measured using GDP growth rates. Sustained economic growth is a key indicator of development and rising living standards.",
  },
  {
    term: "GDP Per Capita",
    tag: "Comparison Dashboard",
    icon: <AttachMoneyIcon />,
    definition:
      "A measure of a country's economic output per person, calculated by dividing GDP by total population. It provides a better indication of individual prosperity than total GDP alone.",
  },
  {
    term: "Digital Infrastructure",
    tag: "Insights · Research Paper",
    icon: <PublicIcon />,
    definition:
      "The foundational technology systems such as internet connectivity, mobile networks, and payment platforms that enable digital transactions. Weak infrastructure is a major contributor to the digital divide.",
  },
  {
    term: "Cash Dependency",
    tag: "Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "The extent to which individuals or businesses rely on physical cash for transactions instead of digital payments. High cash dependency slows financial formalisation and reduces traceability.",
  },
  {
    term: "Data Visualisation",
    tag: "Dashboard · Comparison Dashboard",
    icon: <BarChartIcon />,
    definition:
      "The graphical representation of data through charts, maps, and dashboards. AthEcoScope uses line charts, bar charts, pie charts, and choropleth maps to make complex economic data easier to understand.",
  },
  {
    term: "Choropleth Map",
    tag: "Dashboard",
    icon: <PublicIcon />,
    definition:
      "A type of map where regions are shaded based on data values. On the AthEcoScope Dashboard, Indian states are coloured based on money transferred, with darker shades indicating higher values.",
  },
  {
    term: "User Interface (UI)",
    tag: "Dashboard · Home",
    icon: <SmartphoneIcon />,
    definition:
      "The visual layout and interactive elements of the platform that users engage with, including charts, buttons, sliders, and panels designed for intuitive navigation.",
  },
  {
    term: "Data Normalisation",
    tag: "Dashboard",
    icon: <InsightsIcon />,
    definition:
      "The process of adjusting values to a common scale, often by converting them into an index. This allows meaningful comparison between datasets with different magnitudes.",
  },
  {
    term: "Adoption Curve",
    tag: "Research Paper · Insights",
    icon: <TrendingUpIcon />,
    definition:
      "A model describing how new technologies like UPI spread across populations over time, typically moving from early adopters to the majority and finally laggards.",
  },
  {
    term: "Behavioural Shift",
    tag: "Research Paper",
    icon: <PeopleAltIcon />,
    definition:
      "A change in how individuals act, such as moving from cash-based to digital payments. The research highlights behavioural shifts as a key outcome of UPI adoption.",
  },
    {
    term: "Access to Banking",
    tag: "Research Paper · Insights",
    icon: <AccountBalanceIcon />,
    definition:
      "The ability of individuals to open and use bank accounts. While access has increased significantly in India, the research highlights that usage — not just access — determines true financial inclusion.",
  },
  {
    term: "Behavioural Economics",
    tag: "Research Paper",
    icon: <PeopleAltIcon />,
    definition:
      "A field of economics that studies how psychological factors influence financial decisions. In this research, behavioural shifts toward digital payments are key to understanding UPI adoption.",
  },
  {
    term: "Credit Access",
    tag: "Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "The ability of individuals or businesses to obtain loans or financial support. Improved transaction records through UPI increase creditworthiness for MSMEs and informal workers.",
  },
  {
    term: "Data Reliability",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "The degree to which collected data is accurate and consistent. The research ensures reliability through structured survey design and cross-verification with secondary sources like RBI and World Bank.",
  },
  {
    term: "Economic Disparity",
    tag: "Insights",
    icon: <PublicIcon />,
    definition:
      "The unequal distribution of income and financial access across populations. The digital divide reinforces economic disparity despite high national UPI adoption rates.",
  },
  {
    term: "Financial Behaviour",
    tag: "Research Paper",
    icon: <PeopleAltIcon />,
    definition:
      "Patterns in how individuals manage money, including spending, saving, and payment choices. UPI adoption significantly alters financial behaviour toward digital systems.",
  },
  {
    term: "Growth Rate",
    tag: "Dashboard · Comparison Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "The percentage increase in a metric over time, such as GDP or transaction volume. Growth rates help identify economic momentum and trends.",
  },
  {
    term: "Household Adoption",
    tag: "Research Paper",
    icon: <PeopleAltIcon />,
    definition:
      "The extent to which families adopt and regularly use digital payment systems like UPI. The research shows strong adoption across surveyed households.",
  },
  {
    term: "Informal Economy",
    tag: "Research Paper · Insights",
    icon: <AccountBalanceIcon />,
    definition:
      "Economic activity that is not regulated or taxed by the government. UPI helps formalise parts of the informal economy by creating digital transaction records.",
  },
  {
    term: "Joint Hypothesis Testing",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "A statistical method used to evaluate multiple variables simultaneously. In the research, it is used to assess how UPI adoption and financial inclusion together affect formalisation.",
  },
  {
    term: "Key Indicators",
    tag: "Dashboard",
    icon: <BarChartIcon />,
    definition:
      "Important metrics such as GDP, inflation, and employment rate that summarise economic performance and trends.",
  },
  {
    term: "Liquidity",
    tag: "Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "The ease with which assets can be converted into cash. Digital payments increase liquidity by enabling instant transfers and access to funds.",
  },
  {
    term: "Market Efficiency",
    tag: "Insights",
    icon: <TrendingUpIcon />,
    definition:
      "The extent to which markets operate optimally with minimal friction. Digital payments improve efficiency by reducing transaction time and costs.",
  },
  {
    term: "Network Effects",
    tag: "Research Paper",
    icon: <PublicIcon />,
    definition:
      "A phenomenon where a service becomes more valuable as more people use it. UPI's rapid growth is partly driven by strong network effects.",
  },
  {
    term: "Operational Costs",
    tag: "Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "The expenses associated with running a business. Digital payments reduce operational costs for MSMEs by minimising cash handling and record-keeping burdens.",
  },
  {
    term: "Payment Ecosystem",
    tag: "Insights",
    icon: <SmartphoneIcon />,
    definition:
      "The network of platforms, banks, and users involved in digital transactions. UPI forms the backbone of India's modern payment ecosystem.",
  },
  {
    term: "Quantitative Analysis",
    tag: "Research Paper",
    icon: <BarChartIcon />,
    definition:
      "The use of numerical data and statistical methods to analyse trends and relationships. This research uses regression and index construction as part of its quantitative approach.",
  },
  {
    term: "Rural Inclusion",
    tag: "Insights",
    icon: <PublicIcon />,
    definition:
      "The integration of rural populations into formal financial systems. The research identifies rural areas as key gaps in digital adoption.",
  },
  {
    term: "Statistical Significance",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "A measure indicating whether a result is likely due to a real relationship rather than chance. The regression results confirm significant links between UPI use and formalisation.",
  },
  {
    term: "Transaction Costs",
    tag: "Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "Costs incurred when making an economic exchange. UPI reduces transaction costs by enabling free or low-cost digital payments.",
  },
  {
    term: "User Adoption Rate",
    tag: "Research Paper",
    icon: <TrendingUpIcon />,
    definition:
      "The speed at which users begin using a new technology. UPI has one of the fastest adoption rates globally.",
  },
  {
    term: "Variance",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "A statistical measure of how spread out data values are. It is used in regression analysis to understand data distribution.",
  },
  {
    term: "Wealth Distribution",
    tag: "Insights",
    icon: <PublicIcon />,
    definition:
      "How wealth is spread across a population. Digital financial systems can influence distribution by increasing access to financial tools.",
  },
  {
    term: "X-Factor Variables",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "External variables not directly measured in the model but which may influence outcomes, such as government policy or technological innovation.",
  },
  {
    term: "Yield (Economic)",
    tag: "Insights",
    icon: <TrendingUpIcon />,
    definition:
      "The return generated from an investment or economic activity. Higher efficiency in transactions can indirectly improve economic yield.",
  },
  {
    term: "Zero-Cost Transactions",
    tag: "Insights · Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "Transactions that incur no fees for users. UPI's zero-cost structure is a major driver of its widespread adoption across India.",
  },
  {
    term: "Digital Divide",
    tag: "Research Paper · Insights",
    icon: <PublicIcon />,
    definition:
      "The gap in digital payment adoption between different population groups. On AthEcoScope this refers to rural, elderly, and low-income segments who face barriers including smartphone access, internet connectivity, and digital literacy — meaning high UPI adoption statistics alone do not guarantee deep financial inclusion.",
  },
  {
    term: "Education Literacy",
    tag: "Comparison Dashboard",
    icon: <PeopleAltIcon />,
    definition:
      "A country-level metric on the Comparison Dashboard showing the percentage of the population who can read and write. It is displayed as a horizontal bar chart ranking India, USA, China, Germany, and Brazil alongside a global average of 86%.",
  },
  {
    term: "Employment Rate",
    tag: "Comparison Dashboard",
    icon: <BarChartIcon />,
    definition:
      "Shown on the Comparison Dashboard as a line chart from 2018 to 2028, this is the percentage of a country's population that is employed — calculated by dividing employment (in millions) by total population. The Y-axis is zoomed between 30% and 60% to reveal meaningful differences across countries.",
  },
  {
    term: "Financial Inclusion",
    tag: "Research Paper · Insights",
    icon: <AccountBalanceIcon />,
    definition:
      "A central theme of AthEcoScope. It refers to whether people who have a bank account actually use formal financial services actively and consistently. The research finds that 92.5% of surveyed respondents actively use UPI, and about 1 in 6 identified a UPI-linked platform as their very first meaningful interaction with formal banking.",
  },
  {
    term: "Financial Literacy",
    tag: "Comparison Dashboard",
    icon: <InsightsIcon />,
    definition:
      "A country-level metric shown in the Comparison Dashboard's pie chart, measuring the percentage of the population with sufficient knowledge to make informed financial decisions. It can be toggled with Smartphone Penetration using the dropdown selector on the chart.",
  },
  {
    term: "Focus Mode",
    tag: "Comparison Dashboard",
    icon: <SearchIcon />,
    definition:
      "An interactive feature on the Comparison Dashboard. Clicking a bar, line, or pie slice isolates that single country's data across all charts, fading out the others. A Reset Focus Mode button appears at the top to return to the full multi-country view.",
  },
  {
    term: "Forecast",
    tag: "Dashboard · Comparison Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "Data projections shown for 2026, 2027, and 2028 — displayed with a shaded reference area and dashed lines to visually distinguish them from historical actuals (2018–2025). On the main Dashboard a toggle switch lets you show or hide the forecast band.",
  },
  {
    term: "GDP",
    tag: "Dashboard · Comparison Dashboard · Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "Gross Domestic Product — the total value of goods and services produced by a country, measured in trillions of USD. It is the primary economic indicator on both the Dashboard (India's GDP over time) and the Comparison Dashboard (five countries from 2018–2028). The Dashboard also plots YoY % and MoM % change alongside the raw GDP values.",
  },
  {
    term: "Global Economic Comparison Dashboard",
    tag: "Comparison Dashboard",
    icon: <PublicIcon />,
    definition:
      "A page on AthEcoScope allowing side-by-side comparison of five countries — India, USA, China, Germany, and Brazil — across GDP, Employment Rate, Inflation Rate, Education & Literacy, Financial Literacy, and Smartphone Penetration from 2018 to 2028.",
  },
  {
    term: "Granularity",
    tag: "Dashboard",
    icon: <BarChartIcon />,
    definition:
      "A toggle on the main Dashboard letting you switch the GDP chart between Yearly view (one data point per year) and Monthly view (one data point per month). Selecting Monthly reveals finer fluctuations including month-on-month percentage changes.",
  },
  {
    term: "Housing Index",
    tag: "Dashboard",
    icon: <AccountBalanceIcon />,
    definition:
      "A relative score shown in the State Metrics panel on the Dashboard when a state is selected on the India map. It is a proxy measure of housing conditions within that state, derived from AthEcoScope's state-level dataset.",
  },
  {
    term: "Inflation Rate",
    tag: "Comparison Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "Shown as a line chart on the Comparison Dashboard from 2018 to 2028, this measures how quickly prices increase over time across five countries. Moderate inflation is described as healthy for growth; high or volatile inflation reduces purchasing power and economic stability.",
  },
  {
    term: "Mixed-Methods Design",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "The research methodology used in AthEcoScope's research paper. It combines a structured survey of 114 respondents, composite index construction, OLS (Ordinary Least Squares) regression analysis, and secondary national data from NPCI, RBI, and the World Bank.",
  },
  {
    term: "Money by State (₹ Crores)",
    tag: "Dashboard",
    icon: <AttachMoneyIcon />,
    definition:
      "The primary metric visualised on the India choropleth map on the Dashboard. It shows total money transferred in each state in Indian Rupees (Crores), with darker blue shading indicating higher values. Clicking a state updates the State Metrics panel on the right.",
  },
  {
    term: "MoM % (Month-on-Month)",
    tag: "Dashboard",
    icon: <BarChartIcon />,
    definition:
      "A percentage showing how GDP changed compared to the previous month. Visible on the Dashboard when granularity is set to Monthly. It is plotted as a separate dashed line alongside the GDP bar or line chart.",
  },
  {
    term: "MSMEs",
    tag: "Research Paper · Insights",
    icon: <AccountBalanceIcon />,
    definition:
      "Micro, Small, and Medium Enterprises — a key focus group in AthEcoScope's research. The paper finds that 74.3% of MSME respondents reported greater willingness to accept digital payments after adopting UPI, and 58.6% saw improved transaction record-keeping, strengthening their access to formal credit.",
  },
  {
    term: "Normalize / Index",
    tag: "Dashboard",
    icon: <InsightsIcon />,
    definition:
      "A toggle switch on the Dashboard's GDP chart that, when enabled, re-expresses all GDP values as an index relative to the first actual data year (set to 100). This makes it easier to compare growth rates rather than absolute sizes across time periods.",
  },
  {
    term: "NPCI",
    tag: "Research Paper",
    icon: <AccountBalanceIcon />,
    definition:
      "National Payments Corporation of India — the organisation that operates UPI. It is cited in the research paper as one of the primary secondary data sources, used alongside RBI and World Bank data to study UPI transaction volumes and financial inclusion trends.",
  },
  {
    term: "OLS Regression",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "Ordinary Least Squares regression — a statistical method used in the research paper to measure the relationship between UPI adoption intensity, financial inclusion, and transaction formalisation. The combined model produced an R² of 0.48, explaining 48% of the variation in formalisation outcomes.",
  },
  {
    term: "Population",
    tag: "Comparison Dashboard",
    icon: <PeopleAltIcon />,
    definition:
      "Measured in millions, population data for India, USA, China, Germany, and Brazil from 2018 to 2028 is shown in the GDP & Population data table at the bottom of the Comparison Dashboard, alongside GDP figures and year-on-year percentage changes.",
  },
  {
    term: "Predicted (data region)",
    tag: "Dashboard · Comparison Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "The shaded area on charts covering 2026–2028 indicating forecasted rather than historical data. On the Dashboard the forecast region is labelled and can be toggled on or off. On the Comparison Dashboard it appears as a grey shaded reference area with a dashed divider line.",
  },
  {
    term: "Quick View",
    tag: "Home",
    icon: <BarChartIcon />,
    definition:
      "A summary section on the Home page showing three headline statistics: Total UPI Transactions in 2024 (131 billion transactions worth ₹199 trillion), the Year-on-Year growth rate (+46% from 2023 to 2024), and a Seasonality Snapshot noting that Diwali produces a spike and February sees a dip in transaction volumes.",
  },
  {
    term: "R² (R-squared)",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "A statistical measure of model fit used in the research paper. The combined OLS regression model achieved R² = 0.48, meaning 48% of the variance in transaction formalisation outcomes is explained by the model's variables — UPI adoption intensity and financial inclusion.",
  },
  {
    term: "RBI",
    tag: "Research Paper",
    icon: <AccountBalanceIcon />,
    definition:
      "Reserve Bank of India — the central bank of India, cited in the research paper as one of the key secondary data sources used to study UPI transaction volumes, financial inclusion indicators, and related macroeconomic trends.",
  },
  {
    term: "Regional Spread",
    tag: "Dashboard · Insights",
    icon: <PublicIcon />,
    definition:
      "An insight highlighted on the Home page and visualised through the state-level choropleth map on the Dashboard. It refers to significant state-by-state differences in money transferred, smartphone penetration, urbanisation, and housing — reflecting how policy, infrastructure, and local ecosystems create stark gaps across India.",
  },
  {
    term: "Seasonality",
    tag: "Home · Dashboard",
    icon: <TrendingUpIcon />,
    definition:
      "The pattern of UPI transaction volumes rising and falling at predictable times of year. The Home page's Quick View section highlights that Diwali produces a visible spike in transactions while February shows a dip. These patterns are visible in the Monthly granularity view on the Dashboard.",
  },
  {
    term: "Smartphone Penetration",
    tag: "Comparison Dashboard · Insights",
    icon: <SmartphoneIcon />,
    definition:
      "The percentage of a country's population using smartphones. On the Comparison Dashboard it can be selected in the pie chart dropdown to compare five countries for any year from 2018 to 2028. On the Dashboard it also appears as a state-level metric. The Insights section notes that app bloat and patchy data create friction on low-end Android devices outside metro areas.",
  },
  {
    term: "State Metrics Panel",
    tag: "Dashboard",
    icon: <BarChartIcon />,
    definition:
      "A panel on the right side of the Dashboard that updates when you click a state on the India choropleth map. It displays four metrics for the selected state: Money Transferred (₹ Crores), Smartphone Penetration (%), Urbanisation (%), and Housing Index. When no state is selected, it shows India-wide averages.",
  },
  {
    term: "Survey Respondents",
    tag: "Research Paper",
    icon: <PeopleAltIcon />,
    definition:
      "AthEcoScope's research paper is based on primary data collected from 114 respondents comprising households, MSMEs, and informal sector participants. Key findings include that 92.5% actively use UPI and that pre-adoption digital transactions averaged 28.6% of total payments, rising to 71.3% post-adoption.",
  },
  {
    term: "Transaction Formalisation Index",
    tag: "Research Paper",
    icon: <InsightsIcon />,
    definition:
      "A composite index constructed in the research paper to measure the degree to which a respondent's financial transactions are digitally recorded and traceable. The mean score across 114 respondents was 0.67, reflecting a significant but incomplete shift from cash to formal digital payments.",
  },
  {
    term: "UPI (Unified Payments Interface)",
    tag: "Home · Dashboard · Research Paper",
    icon: <AttachMoneyIcon />,
    definition:
      "The central subject of AthEcoScope. UPI is India's real-time digital payment system that allows instant bank-to-bank transfers via mobile. Launched in 2016, it processed 171 billion transactions in 2025. AthEcoScope's research concludes that UPI adoption is positively and significantly associated with financial inclusion and transaction formalisation in India.",
  },
  {
    term: "UPI Adoption Intensity",
    tag: "Research Paper",
    icon: <TrendingUpIcon />,
    definition:
      "A variable in the research paper's OLS regression model measuring how frequently and deeply respondents use UPI — not just whether they have it installed. Higher adoption intensity is significantly associated with greater financial inclusion and a higher Transaction Formalisation Index score.",
  },
  {
    term: "Urbanisation",
    tag: "Dashboard",
    icon: <PublicIcon />,
    definition:
      "Shown as a percentage in the State Metrics panel on the Dashboard, this represents the share of a state's population living in urban areas. It is one of four proxy metrics that load when a state is clicked on the India choropleth map.",
  },
  {
    term: "Velocity of Transactions",
    tag: "Insights",
    icon: <TrendingUpIcon />,
    definition:
      "An insight on the Home page describing the nature of UPI transaction patterns: small-ticket, high-frequency micro-payments drive daily volume, while the tail of high-value transfers disproportionately lifts total transaction value.",
  },
  {
    term: "Year Slider",
    tag: "Comparison Dashboard",
    icon: <BarChartIcon />,
    definition:
      "An interactive slider on the Comparison Dashboard (range: 2018–2028) that filters the pie chart to show Financial Literacy or Smartphone Penetration values for the selected year across all five countries. Moving the slider updates the chart in real time.",
  },
  {
    term: "YoY % (Year-on-Year)",
    tag: "Dashboard · Home",
    icon: <TrendingUpIcon />,
    definition:
      "The percentage change in a metric compared to the same period in the previous year. On the Dashboard it is plotted as a line alongside the GDP bar or line chart. The Home page highlights a +46% YoY growth in UPI transaction volume from 2023 to 2024 as a headline statistic.",
  },
];

// ── Group terms by first letter ──
function groupByLetter(terms: GlossaryTerm[]) {
  const map: Record<string, GlossaryTerm[]> = {};
  terms.forEach((t) => {
    const l = t.term[0].toUpperCase();
    if (!map[l]) map[l] = [];
    map[l].push(t);
  });
  return map;
}

// ── Stat card (matches Kpi style from Dashboard.tsx) ──
const StatCard: React.FC<{ value: string; label: string; delay?: number }> = ({
  value,
  label,
  delay = 0,
}) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-20px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0) scale(1)" : "translateY(20px) scale(0.96)",
        transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.paper,
          px: 3,
          py: 2,
          textAlign: "center",
          minWidth: 130,
          transition: "transform 0.22s ease, box-shadow 0.22s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 12px 32px rgba(30,58,138,0.10)",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 900,
            fontSize: "2rem",
            lineHeight: 1,
            background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: BRAND.accentGray,
            mt: 0.5,
          }}
        >
          {label}
        </Typography>
      </Card>
    </div>
  );
};

// ── Term card ──
const TermCard: React.FC<{ item: GlossaryTerm; delay?: number }> = ({ item, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: "-30px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${BRAND.border}`,
          bgcolor: BRAND.paper,
          mb: 2,
          overflow: "hidden",
          position: "relative",
          transition: "transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 16px 40px rgba(30,58,138,0.10)",
            borderColor: "rgba(59,130,246,0.35)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.primary})`,
            transform: "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 0.3s ease",
          },
          "&:hover::before": { transform: "scaleX(1)" },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  color: BRAND.secondary,
                  fontSize: 22,
                  lineHeight: 0,
                  transition: "transform 0.2s ease",
                  ".MuiCard-root:hover &": { transform: "scale(1.15) rotate(-5deg)" },
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: BRAND.primary,
                  lineHeight: 1.2,
                }}
              >
                {item.term}
              </Typography>
            </Box>
            <Chip
              label={item.tag}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 700,
                fontFamily: "'DM Sans', sans-serif",
                background: "linear-gradient(90deg, rgba(16,185,129,0.12), rgba(59,130,246,0.12))",
                color: BRAND.accentGray,
                border: "1px solid rgba(16,185,129,0.20)",
                flexShrink: 0,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: BRAND.accentGray,
              lineHeight: 1.78,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.9rem",
            }}
          >
            {item.definition}
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Main component ──
const Glossary: React.FC = () => {
  const [query, setQuery] = useState("");

  const filtered = query.trim()
    ? TERMS.filter(
        (t) =>
          t.term.toLowerCase().includes(query.toLowerCase()) ||
          t.definition.toLowerCase().includes(query.toLowerCase()) ||
          t.tag.toLowerCase().includes(query.toLowerCase())
      )
    : TERMS;

  const grouped = groupByLetter(filtered);
  const letters = Object.keys(grouped).sort();
  const uniqueTags = new Set(TERMS.flatMap((t) => t.tag.split(" · ")));

  return (
    <Box sx={{ bgcolor: BRAND.background, minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
      <StyleInjector />

      {/* ── Hero ── */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, sm: 10 },
          bgcolor: BRAND.paper,
          borderBottom: `1px solid ${BRAND.border}`,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Radial glow */}
        <Box sx={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse 70% 60% at 50% -10%, rgba(125,155,219,0.13) 0%, transparent 70%)`, pointerEvents: "none" }} />

        {/* Floating bg icons */}
        <FloatIcon icon={<AttachMoneyIcon fontSize="inherit" />} size={200} top={-50} left={-40} rot="-20deg" duration={8} />
        <FloatIcon icon={<TrendingUpIcon fontSize="inherit" />} size={120} bottom={10} right={30} color={BRAND.accentGreen} duration={5} />
        <FloatIcon icon={<BarChartIcon fontSize="inherit" />} size={100} top={40} right={"8%"} color={BRAND.secondary} rot="10deg" duration={9} />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Fade in timeout={500}>
            <Typography
              variant="h3"
              fontWeight={900}
              className="fade-up d0"
              sx={{
                fontFamily: "'Calibri', sans-serif",
                fontSize: { xs: "2.2rem", sm: "2.75rem", md: "3.25rem" },
                lineHeight: 1.15,
                mb: 1,
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Welcome to the Glossary
            </Typography>
          </Fade>

          {/* Animated divider (same as Home.tsx) */}
          <Box
            className="fade-up d1"
            sx={{
              width: 80,
              height: 3,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.primary})`,
              mx: "auto",
              mt: 1.5,
              mb: 3,
            }}
          />

          <Fade in timeout={700}>
            <Typography
              variant="h6"
              className="fade-up d2"
              sx={{
                fontWeight: 300,
                color: BRAND.accentGray,
                lineHeight: 1.7,
                maxWidth: 560,
                mx: "auto",
                mb: 4,
                fontFamily: "'Calibri', sans-serif",
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Every term, metric, and concept used across AthEcoScope — explained simply, so you can explore the data, dashboards, and research with full confidence.
            </Typography>
          </Fade>

          {/* Search */}
          <Fade in timeout={900}>
            <Box className="fade-up d3" sx={{ maxWidth: 420, mx: "auto" }}>
              <TextField
                fullWidth
                placeholder="Search terms…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: BRAND.secondarySoft }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: BRAND.background,
                    fontFamily: "'Calibri', sans-serif",
                    "&:hover fieldset": { borderColor: BRAND.secondary },
                    "&.Mui-focused fieldset": { borderColor: BRAND.secondary },
                  },
                }}
              />
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* ── Stats strip ── */}
      <Box sx={{ py: 3, borderBottom: `1px solid ${BRAND.border}`, bgcolor: BRAND.paper }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            <Grid item><StatCard value={String(TERMS.length)} label="Total Terms" delay={0} /></Grid>
            <Grid item><StatCard value={String(uniqueTags.size)} label="Topic Areas" delay={100} /></Grid>
            <Grid item><StatCard value={String(new Set(TERMS.map((t) => t.term[0].toUpperCase())).size)} label="Letters Covered" delay={200} /></Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Main body ── */}
      <Box component="section" sx={{ py: { xs: 5, md: 7 }, flexGrow: 1 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>

            {/* Sidebar letter nav */}
            <Grid item xs={12} md={2}>
              <Box sx={{ position: { md: "sticky" }, top: 24 }}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "#9ca3af",
                    mb: 1,
                    pl: 1,
                  }}
                >
                  Browse by letter
                </Typography>
                <Box sx={{ display: "flex", flexDirection: { xs: "row", md: "column" }, flexWrap: "wrap", gap: "2px" }}>
                  {Object.keys(groupByLetter(TERMS)).sort().map((l) => (
                    <Box
                      key={l}
                      component="a"
                      href={`#letter-${l}`}
                      sx={{
                        fontFamily: "'Calibri', sans-serif",
                        fontSize: "13px",
                        color: BRAND.accentGray,
                        textDecoration: "none",
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        borderLeft: { md: "2px solid transparent" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "all 0.15s",
                        "&:hover": {
                          bgcolor: "#f0f5ff",
                          color: BRAND.primary,
                          borderLeftColor: BRAND.secondary,
                        },
                      }}
                    >
                      <span>{l}</span>
                      <Chip
                        label={grouped[l]?.length ?? 0}
                        size="small"
                        sx={{ height: 16, fontSize: "10px", bgcolor: BRAND.background, color: "#9ca3af", ml: 0.5 }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Terms */}
            <Grid item xs={12} md={10}>
              {filtered.length === 0 ? (
                <Box textAlign="center" py={8}>
                  <Typography
                    sx={{ fontFamily: "'Calibri', sans-serif", fontSize: "1.4rem", color: BRAND.primary, mb: 1 }}
                  >
                    No matching terms found
                  </Typography>
                  <Typography sx={{ color: "#9ca3af", fontSize: "14px" }}>
                    Try a different keyword or browse by letter.
                  </Typography>
                </Box>
              ) : (
                letters.map((letter) => (
                  <Box key={letter} id={`letter-${letter}`} sx={{ mb: 4 }}>
                    {/* Letter heading */}
                    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.5, mb: 2, pb: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "'Calibri', sans-serif",
                          fontWeight: 900,
                          fontSize: "3rem",
                          lineHeight: 1,
                          background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.secondary})`,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {letter}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "#9ca3af",
                          mb: 1,
                        }}
                      >
                        {grouped[letter].length} {grouped[letter].length === 1 ? "term" : "terms"}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2, background: `linear-gradient(90deg, ${BRAND.secondary}, ${BRAND.accentGreen})`, height: 2, border: "none", borderRadius: 1 }} />
                    {grouped[letter].map((item, i) => (
                      <TermCard key={item.term} item={item} delay={i * 60} />
                    ))}
                  </Box>
                ))
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer gradient strip (same as About.tsx) */}
      <Box sx={{ height: 8, background: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.secondary})` }} />
    </Box>
  );
};

export default Glossary;